/**
 * Everyone involved in a ticket, for the « Intervenants » tab of the message
 * window. Built from what the window already loads (the ticket, its messages
 * with their ``origin_kind`` / ``outside_source`` from the API, the assigned
 * employee, the AI agent's name) plus the carriers the ``/actors`` endpoint
 * lists. Pure: no fetching, no rendering.
 */

const NOT_ASSIGNED = new Set(['', 'not assigned', 'n/a', 'non assigné']);
const SENDER_PREFIXES = /^(customer_|admin_|provider_)/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isEmailAddress = (value) => EMAIL.test(String(value || '').trim());

const normalize = (value) => String(value || '').trim().toLowerCase();

/** The address a message's sender is known by (customer_ / admin_ /
 *  provider_ prefixes removed), or '' when it is not an address. */
export const senderAddress = (message) => {
    const fromMetadata = message?.metadata?.email;
    if (isEmailAddress(fromMetadata)) return normalize(fromMetadata);
    const raw = typeof message?.sender_id === 'string' ? message.sender_id.replace(SENDER_PREFIXES, '') : '';
    return isEmailAddress(raw) ? normalize(raw) : '';
};

/** Who wrote a team message outside Unanimity: the external system's staff
 *  name, else their address. */
export const outsideAuthor = (message) => {
    const admin = message?.metadata?.admin_info;
    if (admin && typeof admin === 'object' && (admin.name || admin.email)) {
        return String(admin.name || admin.email).trim();
    }
    return senderAddress(message);
};

// English defaults of the places a team message can be written outside
// Unanimity (``outside_source``); an unknown integration key shows as is.
const OUTSIDE_SOURCE_DEFAULTS = {
    outlook: 'Outlook',
    outlook_sent_items: 'Outlook',
    edn: 'EDN',
    spacefill: 'Spacefill',
    provider: 'external tool',
    external_ticketing: 'external ticketing tool',
    mail: 'e-mail',
};

export const outsideSourceLabel = (t, source) => {
    const key = String(source || 'mail').toLowerCase();
    const fallback = OUTSIDE_SOURCE_DEFAULTS[key] || key.toUpperCase();
    return OUTSIDE_SOURCE_DEFAULTS[key] ? t(`messageWindow.origin.sources.${key}`, fallback) : fallback;
};

const bump = (map, key, make) => {
    const entry = map.get(key) || make();
    entry.count += 1;
    map.set(key, entry);
    return entry;
};

export function buildTicketActors({
    ticket,
    messages,
    employeeName,
    aiAgentName,
    carriers,
    carrierConversation,
} = {}) {
    const items = (Array.isArray(messages) ? messages : []).filter((m) => m && typeof m.origin_kind === 'string');

    const primary = normalize(ticket?.customer_email || ticket?.sender_info_email);
    const customers = new Map();
    if (primary) customers.set(primary, { email: primary, primary: true, count: 0 });

    const fromUnanimity = new Map();
    const outside = new Map();
    let aiCount = 0;

    items.forEach((message) => {
        switch (message.origin_kind) {
            case 'customer': {
                const address = senderAddress(message);
                if (address) bump(customers, address, () => ({ email: address, primary: false, count: 0 }));
                break;
            }
            case 'team_unanimity': {
                const name = String(message.sent_by_name || '').trim();
                if (name && !message.is_instructions) {
                    bump(fromUnanimity, name.toLowerCase(), () => ({ name, count: 0 }));
                }
                break;
            }
            case 'team_outside': {
                const name = outsideAuthor(message);
                const source = message.outside_source || 'mail';
                bump(outside, `${name.toLowerCase()}|${source}`, () => ({ name, source, count: 0 }));
                break;
            }
            case 'ai':
                aiCount += 1;
                break;
            default:
                break;
        }
    });

    const assignedName = String(employeeName || '').trim();
    const assigned = NOT_ASSIGNED.has(assignedName.toLowerCase()) ? null : assignedName;

    const agentName = String(aiAgentName || '').trim();
    const ai = agentName || aiCount ? { name: agentName || null, count: aiCount } : null;

    let carrierList = Array.isArray(carriers)
        ? carriers.filter((c) => c && c.name).map((c) => ({
            name: c.name,
            status: c.status || null,
            contactedAt: c.contacted_at || null,
            conversations: c.conversations || 1,
        }))
        : [];
    if (!carrierList.length && carrierConversation?.carrier_name) {
        carrierList = [{
            name: carrierConversation.carrier_name,
            status: carrierConversation.status || null,
            contactedAt: null,
            conversations: carrierConversation.conversations_count || 1,
        }];
    }

    return {
        customers: Array.from(customers.values()),
        team: {
            fromUnanimity: Array.from(fromUnanimity.values()),
            outside: Array.from(outside.values()),
            assigned,
        },
        ai,
        carriers: carrierList,
    };
}

export default buildTicketActors;
